import { useEffect } from 'react';

type Callback = (entry: IntersectionObserverEntry) => void;

const sharedObserverCallbacks = new WeakMap<Element, Callback>();

let sharedObserver: IntersectionObserver | null = null;

const classInitializeRatio = 0.1;

function initObserver(): IntersectionObserver | null {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        return null;
    }

    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const cb = sharedObserverCallbacks.get(entry.target);
                    if (cb) {
                        cb(entry);
                    }
                });
            },
            {
                threshold: [classInitializeRatio, 1],
            }
        );
    }

    return sharedObserver;
}

export function useDomInView(
    ref: React.RefObject<HTMLElement>,
    className: string,
    fullyInViewClassName?: string,
    shouldTrackInView: boolean = false,
) {
    useEffect(() => {

        const el = ref.current;

        if (!el) return;

        if (!shouldTrackInView) {
            el.classList.add(className);
            if (fullyInViewClassName) {
                el.classList.add(fullyInViewClassName);
            }
            return;
        }

        const observer = initObserver();

        if (!observer) {
            el.classList.add(className);
            if (fullyInViewClassName) {
                el.classList.add(fullyInViewClassName);
            }
            return;
        }

        const callback: Callback = (entry) => {
            if (entry.isIntersecting) {
                el.classList.add(className);
            } else {
                el.classList.remove(className);
            }

            if (fullyInViewClassName && entry.intersectionRatio >= classInitializeRatio) {
                el.classList.add(fullyInViewClassName);
            }
        };

        sharedObserverCallbacks.set(el, callback);
        observer.observe(el);

        return () => {
            observer.unobserve(el);
            sharedObserverCallbacks.delete(el);
        };
    }, [className, fullyInViewClassName]);
}